import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import {
  Star,
  CalendarArrowDown,
  CalendarArrowUp,
  HandPlatter,
  MapPin,
  Users,
  DollarSign,
} from "lucide-react";
import { format } from "date-fns";
import ImagePreview from "../../modules/ui/ImagePreview";
import { JOINED_GROUP_ORDER, FETCH_GROUP_ORDER_DASHBOARD } from "../../store/GroupOrder/GroupOrder.constant";
import { assignGroupOfGroupOrderApi, duplicateGroupOrderApi } from "../../shared/services/Api/Report";
import AnimatedLoader from "../../modules/ui/animatedLoader";
import ModalCloneConfirmation from "../../modules/ui/Modals/ModalCloneConfirmation";

const Card = ({
  order_deadline,
  joined,
  slug_uuid,
  delivery_date_time,
  id,
  search_result_image_url,
  name,
  slug,
  restaurant_name,
  group_order_group,
  status,
  groupData,
  is_dashboard_admin,
  city,
  short_address,
  joined_member_count,
  head_count,
  per_person_budget,
}) => {
  const hideEdit = status === "waiting_for_delivery" || status === "delivered";
  const cardCity = city || short_address;
  const participantCount = joined_member_count ?? head_count;
  const cardBudgetCents = per_person_budget?.cents;
  const dispatch = useDispatch();
  const history = useHistory();
  const onClickJoined = useCallback(() => {
    dispatch({
      type: JOINED_GROUP_ORDER,
      payload: { id, slug, history, slug_uuid },
    });
  }, [id, slug, history, slug_uuid, dispatch]);
  const link = `/group_order/${slug || id}?creator_id=${slug_uuid}`;


  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const dropdownRef = useRef(null);
  useEffect(() => {
    if (group_order_group?.id) {
      setSelectedGroup(group_order_group)
    };
  }, [group_order_group]);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onHandleSelect = async (group) => {
    setOpen(false);
    const response = await assignGroupOfGroupOrderApi({ group_order_id: id, group_id: group?.id });
    if (response) {
      setSelectedGroup(group);
    }
  };

  const onHandleClone = () => {
    setShowCloneModal(true);
  };

  const handleCloneConfirm = async () => {
    setShowCloneModal(false);
    setLoading(true);
    try {
      const response = await duplicateGroupOrderApi({ group_order_id: id });
      if (response?.group_order?.uuid) {
        history.push(`/${response?.group_order?.uuid}`);
      }
    } finally {
      setLoading(false);
    }
  }

  const onHandleEdit = () => {
    window.open(`/${slug}`, "_blank");
  };

  return (
    <>
      <AnimatedLoader isLoading={loading} /> 
      <ModalCloneConfirmation
        show={showCloneModal}
        onConfirm={handleCloneConfirm}
        onClose={() => setShowCloneModal(false)}
      />   
      <div className="card-outer">
      {
        is_dashboard_admin && <div className="joined-group-dropdown">
          <button
            onClick={onHandleEdit}
            style={{
              padding: "0px 9px",
              border: "1px solid rgb(204, 204, 204)",
              borderRadius: "6px",
              background: " rgb(249, 249, 249)",
              cursor: "pointer",
              marginTop: "159px",
              marginRight: "75px",
              color: "#fff",
              backgroundColor: "var(--brand-primary)",
            }}
          >
            Edit
          </button>
        </div>
      }
      {
        is_dashboard_admin && <div className="joined-group-dropdown">
          <button
            onClick={onHandleClone}
            style={{
              padding: "0px 9px",
              border: "1px solid rgb(204, 204, 204)",
              borderRadius: "6px",
              background: " rgb(249, 249, 249)",
              cursor: "pointer",
              marginTop: "159px",
              color: "#fff",
              backgroundColor: "var(--brand-primary)",
            }}
          >
            Clone
          </button>
        </div>
      }
      {
        is_dashboard_admin && !joined && <div ref={dropdownRef} className="joined-group-dropdown">
          <button
            onClick={() => setOpen(!open)}
            style={{
              padding: "8px 12px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              background: "#f9f9f9",
              cursor: "pointer",
            }}
          >
            {selectedGroup?.name ?? 'Add Group ▼'}
          </button>

          {open && (
            <ul
              style={{

              }}
            >
              <li
                onClick={() => onHandleSelect(null)}
                  style={{
                    cursor: "pointer",
                  }}
              >
                No group (open access)
              </li>
              {groupData?.map((item, index) => (
                <li
                  onClick={() => onHandleSelect(item)}
                  key={item?.id}
                  className="p-2"
                  style={{
                    wordBreak: 'break-all',
                    borderBottom: index === groupData.length - 1 ? 'none' : '1px solid #cccccc',
                  }}
                >
                  {item?.name}
                </li>
              ))}
            </ul>
          )}
        </div>
      }
      <div className="card-inner">
        <span className={`status-ribbon ${hideEdit ? "archived" : ""}`}>
          {hideEdit ? "Archived" : "Active Now"}
        </span>
        {hideEdit ? (
          // Show only "View Order" if status is "delivered"
          <Link
            to={link}
            target="_self"
            className="btn-edit"
          >
            View Order
          </Link>
        ) : (
          // Show "Joined" or "Yet to Join" / "Join" only when status is NOT "delivered"
          <>
            <div>
              <button className={`btn-joined ${joined ? "" : "active"}`}>
                {joined ? (
                  <>
                    <Star />
                    Joined
                  </>
                ) : (
                  <div onClick={onClickJoined} style={{ fontSize: "15px" }}>
                    Yet to Join
                  </div>
                )}
              </button>

            </div>

            {joined ? (
              <Link
                to={link}
                target="_self"
                className="btn-edit"
              >
                View Order
              </Link>
            ) : (
              <div
                onClick={onClickJoined}
                className="btn-edit d-flex align-items-center justify-content-center"
                style={{ fontSize: "14px", gap: "7px" }}
              >
                <HandPlatter style={{ opacity: "0.6" }} />
                Join
              </div>
            )}
          </>
        )}
        {cardCity ? <span className="city-badge">{cardCity}</span> : null}
        <div>
          <ImagePreview
            image={search_result_image_url}
            defaultImage={"/img/orderimage3.png"}
            alt="modalimage"
            style={{ borderRadius: "0 0 10px 10px" }}
          />
        </div>
      </div>
      <div className="bottom-inner">
        <h2>{name}</h2>
        <p>
          <MapPin />
          {restaurant_name}
        </p>
        {(participantCount != null || cardBudgetCents != null) && (
          <div className="card-meta-row">
            {participantCount != null ? (
              <span>
                <Users /> {participantCount} Participant
                {participantCount === 1 ? "" : "s"}
              </span>
            ) : null}
            {cardBudgetCents != null ? (
              <span>
                <DollarSign /> {(cardBudgetCents / 100).toFixed(2)} budget
              </span>
            ) : null}
          </div>
        )}
      </div>
      <div className="bottom-outer">
        <div className="d-flex align-items-end order-date">
          <div>
            <div className="d-flex align-items-center justify-content-center calendar-heading">
              <CalendarArrowDown />
              <p className="text-center">Submit Order Before:</p>
            </div>
            <h2 className="text-center br-white">
              {format(
                new Date(order_deadline * 1000),
                "EEEE, MMMM do,yyyy, h:mm aaa"
              )}
            </h2>
          </div>
          <div>
            <div className="d-flex align-items-center justify-content-center calendar-heading">
              <CalendarArrowUp />
              <p className="text-center">Order Delivery On:</p>
            </div>
            <h2 className="text-center br-white">
              {format(
                new Date(delivery_date_time * 1000),
                "EEEE, MMMM do,yyyy, h:mm aaa"
              )}
            </h2>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Card;
